import { Select } from 'antd';
import { useImperativeHandle, forwardRef } from 'react';
import { useGlobal } from '@/utils/useGlobal';
import EFEmail from '@/components/UI/svg/Footer/Icon/EFEmail';
import SelectSuffixIcon from '@/components/UI/svg/Delta2/Icon/SelectSuffixIcon';
import mailchimpCss from '@/components/SignUpForm/Mailchimp/index.module.scss';

interface IExtraField {
    type: string;
    name: string;
    defaultValue?: string;
    data?: any[];
}
interface IKlaviyoMap {
    [key: string]:
        | {
              type: 'klaviyo';
              list_id: string;
              source: string;
              Component: React.FC<{ list_id: string }>;
          }
        | {
              type: 'mailchimp';
              template?: string;
              scriptUrl?: string;
              action: string;
              Component: React.ForwardRefExoticComponent<IMailchimpViewProps>;
              extraFields?: IExtraField[];
          };
}
interface IMailchimpViewProps {
    action: string;
    extraFields?: IExtraField[];
}

const MailchimpViewRaw = ({ action, extraFields }: IMailchimpViewProps, ref: any) => {
    const { useLang, useState, isMobile } = useGlobal();
    const { t } = useLang('shared');
    const { t: td } = useLang('delta2portablepowerstation');
    const [selectVal, setSelectVal] = useState(null);

    const resetFiled = () => setSelectVal(null);

    useImperativeHandle(ref, () => ({
        resetFiled,
    }));

    const extraItem = extraFields?.map((field, i) => {
        const { type, name, data, defaultValue } = field;

        const onChange = (value: string) => setSelectVal(value);

        if (!type) return null;
        if (type === 'hidden_input') {
            return <input name={name} id={`mce-${name}`} value={defaultValue} style={{ display: 'none' }} key={i} />;
        }

        if (type === 'select') {
            const options = data?.map((value, i) => ({
                key: value + i,
                label: value,
                value: value,
            }));
            return (
                <div className="mc-field-group select" data-name={name} key={i}>
                    <input value={selectVal} name={name} className="required cus-hidden" id={`mce-${name}`} aria-required="true" />
                    <Select
                        value={selectVal}
                        className={isMobile ? 'select select-h5' : 'select'}
                        options={options}
                        onChange={onChange}
                        placeholder="Country"
                        listItemHeight={54}
                        suffixIcon={<SelectSuffixIcon />}
                        dropdownClassName={mailchimpCss.dropClass}
                    />
                    <div className="custom_error_msg" style={{ display: 'none' }}></div>
                </div>
            );
        }
    });

    return (
        <div id="mc_embed_signup" className={mailchimpCss.mailchimp}>
            <form
                action={action}
                method="post"
                id="mc-embedded-subscribe-form"
                name="mc-embedded-subscribe-form"
                className="validate"
                target="_blank"
                noValidate
            >
                <div id="mc_embed_signup_scroll">
                    <div className="main">
                        {extraItem}
                        <div className="mc-field-group mc-input" data-name={'EMAIL'}>
                            <span className="email-icon">
                                <EFEmail />
                            </span>
                            <input id="mce-EMAIL" name="EMAIL" type="email" className="required email" placeholder={t('common.email_holder')} />
                            <div className="custom_error_msg" style={{ display: 'none' }}></div>
                        </div>
                        <div className="mc-action">
                            <button type="submit" name="subscribe" id="mc-embedded-subscribe" className="button">
                                {td('delta2.subscribe')}
                            </button>
                        </div>
                    </div>

                    <div id="custom-responses-msg" style={{ display: 'none' }}></div>
                    {/* raw validate */}
                    <div id="mce-responses" className="clear">
                        <div className="response" id="mce-error-response" style={{ display: 'none' }}></div>
                        <div className="response" id="mce-success-response" style={{ display: 'none' }}></div>
                    </div>
                    <div style={{ position: 'absolute', left: '-5000px' }} aria-hidden="true">
                        <input type="text" name="b_d002bf04842356c4c1c5221cf_bfd2f8459d" tabIndex={-1} value="" />
                    </div>
                </div>
            </form>
        </div>
    );
};

const MailchimpView = forwardRef(MailchimpViewRaw);

const klaviyoMap: IKlaviyoMap = {
    us: {
        type: 'mailchimp',
        Component: MailchimpView,
        action: 'https://ecoflow.us19.list-manage.com/subscribe/post?u=80218c17c3ffb8d06454fa749&id=246cdcd9be&f_id=001388e4f0',
    },
    za: {
        type: 'mailchimp',
        Component: MailchimpView,
        action: 'https://ecoflow.us19.list-manage.com/subscribe/post?u=80218c17c3ffb8d06454fa749&amp;id=b0aa922437&amp;f_id=00fe92e4f0',
    },
    ca: {
        type: 'mailchimp',
        Component: MailchimpView,
        action: 'https://ecoflow.us19.list-manage.com/subscribe/post?u=80218c17c3ffb8d06454fa749&id=14657d9581&f_id=001b88e4f0',
    },
    uk: {
        type: 'mailchimp',
        Component: MailchimpView,
        action: 'https://ecoflow.us19.list-manage.com/subscribe/post?u=80218c17c3ffb8d06454fa749&id=3bf5367bfe',
        extraFields: [{ type: 'hidden_input', name: 'COUNTRY', defaultValue: 'United Kingdom' }],
    },
    eu: {
        type: 'mailchimp',
        Component: MailchimpView,
        action: 'https://ecoflow.us19.list-manage.com/subscribe/post?u=80218c17c3ffb8d06454fa749&id=3bf5367bfe',
        extraFields: [
            {
                type: 'select',
                name: 'COUNTRY',
                data: [
                    'Albania',
                    'Algeria',
                    'Andorra',
                    'Australia',
                    'Austria',
                    'Belarus',
                    'Belgium',
                    'Bosnia and Herzegovina',
                    'Bulgaria',
                    'Canada',
                    'Croatia',
                    'Czech Republic',
                    'Denmark',
                    'Estonia',
                    'Finland',
                    'France',
                    'Germany',
                    'Greece',
                    'Hungary',
                    'Iceland',
                    'Ireland',
                    'Italy',
                    'Japan',
                    'Latvia',
                    'Liechtenstein',
                    'Lithuania',
                    'Luxembourg',
                    'Malta',
                    'Moldova, Republic of',
                    'Monaco',
                    'Morocco',
                    'Netherlands',
                    'New Zealand',
                    'Norway',
                    'Poland',
                    'Portugal',
                    'Republic of Kosovo',
                    'Romania',
                    'Serbia',
                    'Slovakia',
                    'Slovenia',
                    'Spain',
                    'Sweden',
                    'Switzerland',
                    'Turkey',
                    'Ukraine',
                    'United Arab Emirates',
                    'United Kingdom',
                    'United States',
                    'Vatican City State (Holy See)',
                    'Other',
                ],
            },
        ],
    },
    de: {
        type: 'mailchimp',
        Component: MailchimpView,
        action: 'https://ecoflow.us19.list-manage.com/subscribe/post?u=80218c17c3ffb8d06454fa749&id=3bf5367bfe',
        extraFields: [{ type: 'hidden_input', name: 'COUNTRY', defaultValue: 'Germany' }],
    },
    fr: {
        type: 'mailchimp',
        Component: MailchimpView,
        action: 'https://ecoflow.us19.list-manage.com/subscribe/post?u=80218c17c3ffb8d06454fa749&id=3bf5367bfe',
        extraFields: [{ type: 'hidden_input', name: 'COUNTRY', defaultValue: 'France' }],
    },
    kr: {
        type: 'mailchimp',
        Component: MailchimpView,
        action: 'https://ecoflow.us14.list-manage.com/subscribe/post?u=60f049f50357a87af9abdbbb2&amp;id=1b4a0b9b7c&amp;f_id=005e9de0f0',
    },
};

export const getCurrTemplate = (locale: string) => klaviyoMap[locale] || {};
